import { MedplumClient } from "@medplum/core";
import type { Flag } from "@medplum/fhirtypes";

import * as Sentry from "@sentry/react";

import { setOrganizationAccountsOnResource } from "@/utils/accounts";
import { getOrCreateResource } from "@/utils/medplum";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import { FlagFailure } from "../constants/flag-failures";
import { PracticeFlag } from "../interfaces/flags";
import {
  buildPracticeFlagResource,
  buildUpdatedPracticeFlagResource,
  getLibraryFlagCode,
  toPracticeFlag,
} from "../utils/practice-flag";
import { PracticeFlagFormType } from "../zod-schemas/flags";

import { countChartsByLibraryFlag } from "./client-flag-counts";
import {
  buildLibraryFlagCodeQuery,
  loadPracticeLibrary,
  readLibraryFlag,
  resolveLibraryGroupReference,
} from "./practice-flag-access";

/**
 * `LookupFailed` is the Group read the create depends on. `DuplicateName` covers an entry that was
 * already there and one a concurrent save got in first with — both arrive the same way, as the
 * conditional create matching instead of writing. `WriteFailed` leaves nothing created;
 * `AccountsFailed` leaves the Flag created but outside the practice compartment.
 */
export type CreatePracticeFlagFailure =
  | FlagFailure.LookupFailed
  | FlagFailure.MissingLibraryGroup
  | FlagFailure.DuplicateName
  | FlagFailure.WriteFailed
  | FlagFailure.AccountsFailed;

/**
 * `reason` is what the caller acts on: it picks the message, and from 1d onward decides whether the
 * failure belongs on a field. The underlying error never crosses this boundary — it is reported to
 * Sentry here, where the context that makes it legible is still in scope.
 */
export type CreatePracticeFlagResult =
  | { ok: true; data: Flag }
  | { ok: false; reason: CreatePracticeFlagFailure };

interface CreatePracticeFlagProps {
  medplum: MedplumClient;
  /** Values that have been through `PracticeFlagFormSchema.parse` — trimmed, not raw form state. */
  values: PracticeFlagFormType;
  practiceId: string;
}

const MISSING_LIBRARY_FLAG_CODE_ERROR = "Cannot guard a library flag write without a derived code";

/** A read, so `LookupFailed` means the Group lookup or the library search — nothing is known yet. */
export type GetPracticeFlagsFailure = FlagFailure.LookupFailed | FlagFailure.MissingLibraryGroup;

export type GetPracticeFlagsResult =
  | { ok: true; data: PracticeFlag[] }
  | { ok: false; reason: GetPracticeFlagsFailure };

/**
 * Reads a practice's whole flag library as the rows the table renders.
 *
 * The whole library comes back in one search, and filtering, sorting and paging all happen in the
 * page. That is the right shape here and not a shortcut: the category counts on the filter chips
 * are counts over the entire library, so the page needs all of it in hand regardless, and a
 * curated set of this size is cheaper to fetch once than to page through.
 *
 * The `On Charts` counts are read alongside the library rather than after it, so the page still
 * costs one round trip more than the Group lookup it already needed. A count that cannot be read
 * leaves every row's `chartCount` as `null` instead of failing the page: the library is what the
 * user came for, and a column that reads "unknown" is better than an error over a list that is
 * otherwise fine.
 *
 * Never throws, mirroring the writes below: every step that can reject becomes a `reason`, so a
 * caller can drive the UI from the result alone.
 *
 * @returns `{ ok: true, data }` with a row per library entry, or `{ ok: false, reason }`
 */
export async function getPracticeFlags({
  medplum,
  practiceId,
}: {
  medplum: MedplumClient;
  practiceId: string;
}): Promise<GetPracticeFlagsResult> {
  const [library, chartCounts] = await Promise.all([
    loadPracticeLibrary<GetPracticeFlagsFailure>(medplum, practiceId, {
      lookupFailed: FlagFailure.LookupFailed,
      missingLibraryGroup: FlagFailure.MissingLibraryGroup,
    }),
    countChartsByLibraryFlag(medplum, practiceId),
  ]);

  if (!library.ok) {
    return { ok: false, reason: library.reason };
  }

  return {
    ok: true,
    // An entry on no charts is absent from the map rather than present as 0, so a missing key and a
    // zero mean the same thing — but only once the counts were read at all.
    data: library.flags.map((flag) => ({
      ...toPracticeFlag(flag),
      chartCount: chartCounts ? (chartCounts.get(flag.id) ?? 0) : null,
    })),
  };
}

/**
 * Creates a flag library entry for a practice, bound to its Flag Library Group as `subject`.
 *
 * The Group is looked up rather than created: provisioning it belongs to practice onboarding and
 * the backfill migration, so a practice without one is a data problem the caller should surface
 * instead of papering over.
 *
 * Deliberately does not call `ensureFlagLibraryGroup`, despite that being the export written for
 * callers with no accounts step of their own. Healing the gap on a user's first save would hide the
 * fact that onboarding or the backfill missed this practice; we want that visible. Reach for
 * `ensureFlagLibraryGroup` from provisioning code, and for `fetchFlagLibraryGroup` from here.
 *
 * Scoping the Flag to the practice compartment is a second write that Medplum only accepts after
 * the resource exists, so it cannot be folded into the create. It gets its own failure reason
 * rather than collapsing into `WriteFailed`: a Flag that exists but is unscoped is invisible to the
 * library, and telling the user to "try again" would only add a second one.
 *
 * A code is unique among a practice's active entries, and the conditional create is what enforces
 * it — see `buildLibraryFlagCodeQuery`. No read precedes it: the guard already answers whether the
 * code exists, so reading the library first would only spend a round trip to learn the same
 * thing. Every library entry is written through here, so every one carries the identifier the guard
 * matches on; nothing predates it and there is no backfill for it to miss.
 *
 * Never throws. Every step that can reject is captured into a `reason`, so callers can drive the UI
 * from the result alone.
 *
 * @returns `{ ok: true, data }` with the created Flag, or `{ ok: false, reason }`
 */
export async function createPracticeFlag({
  medplum,
  values,
  practiceId,
}: CreatePracticeFlagProps): Promise<CreatePracticeFlagResult> {
  const libraryGroup = await resolveLibraryGroupReference<CreatePracticeFlagFailure>(
    medplum,
    practiceId,
    {
      lookupFailed: FlagFailure.LookupFailed,
      missingLibraryGroup: FlagFailure.MissingLibraryGroup,
    },
  );

  if (!libraryGroup.ok) {
    return { ok: false, reason: libraryGroup.reason };
  }

  const groupReference = libraryGroup.reference;
  const flagToCreate = buildPracticeFlagResource(values, groupReference);

  const code = getLibraryFlagCode(flagToCreate);

  // Unreachable for parsed values — the schema rejects a name that slugifies to nothing — but a
  // guard query missing its identifier would match the whole library and read a first save as a
  // duplicate, so an absent code fails the write rather than risking that.
  if (!code) {
    Sentry.captureException(new Error(MISSING_LIBRARY_FLAG_CODE_ERROR));
    return { ok: false, reason: FlagFailure.WriteFailed };
  }

  let flag: Flag;
  let created: boolean;
  try {
    [flag, created] = await getOrCreateResource(
      medplum,
      flagToCreate,
      buildLibraryFlagCodeQuery(groupReference, code),
    );
  } catch (error) {
    captureExceptionWithoutPhi("Failed to create a practice library flag", error, { practiceId });
    return { ok: false, reason: FlagFailure.WriteFailed };
  }

  // The guard matched, so this code is already in the practice's library — either from an earlier
  // save or from a concurrent one that got there first. Nothing was written, and `flag` is that
  // other entry. Uniqueness is on the derived code, not the typed name, so "Fall Risk" and
  // "fall risk!" collide here: both slugify to `fall-risk`.
  if (!created) {
    return { ok: false, reason: FlagFailure.DuplicateName };
  }

  // Medplum takes the accounts call only once the resource exists, so it follows the create rather
  // than riding along with it. It gets its own reason because the Flag is written and valid — it is
  // simply outside the practice compartment — and a retry would add a second one.
  try {
    await setOrganizationAccountsOnResource(medplum, flag, practiceId);
  } catch (error) {
    // The flag id is the only handle an operator has on a Flag the practice cannot see, so it rides
    // along with the report.
    captureExceptionWithoutPhi("Failed to set accounts on a practice library flag", error, {
      flagId: flag.id,
    });
    return { ok: false, reason: FlagFailure.AccountsFailed };
  }

  medplum.invalidateSearches("Flag");

  return { ok: true, data: flag };
}

/**
 * `LookupFailed` is the Group read or the entry read. `WriteFailed` leaves the entry unchanged.
 * There is no `AccountsFailed`: the entry is already in the compartment and the update carries its
 * `meta` across.
 */
export type UpdatePracticeFlagFailure =
  | FlagFailure.LookupFailed
  | FlagFailure.MissingLibraryGroup
  | FlagFailure.NotFound
  | FlagFailure.DuplicateName
  | FlagFailure.WriteFailed;

export type UpdatePracticeFlagResult =
  | { ok: true; data: Flag }
  | { ok: false; reason: UpdatePracticeFlagFailure };

interface UpdatePracticeFlagProps extends CreatePracticeFlagProps {
  /** Logical id of the entry being edited, as the library row carries it. */
  flagId: string;
}

/**
 * Rewrites a library entry from edited form values.
 *
 * The stored entry is replaced rather than merged: a category the user cleared has to actually drop
 * its coding, and merging would keep it. `buildUpdatedPracticeFlagResource` carries over the three
 * things the form does not own — the logical id, `meta` (which is what keeps the entry in the
 * practice compartment), and `status`.
 *
 * A rename re-derives the code, so the invariant a create establishes — one name, one code — holds
 * for the life of an entry, and the library can never show two rows under the same name. That makes
 * the code a value that moves with the name rather than a stable key: join chart flags to
 * `Flag/{id}`, never to the code.
 *
 * Uniqueness is checked only when the code actually changes. A create serializes on the
 * `If-None-Exist` guard and cannot race, but a rename is a read followed by a write, so a
 * simultaneous rename onto the same code can still slip through — narrow enough to accept for a
 * curated per-practice list, and visible as two rows rather than as lost data. Editing only the
 * description skips the check entirely: it costs a round trip, and it would block an unrelated edit
 * on a collision the user has no way to act on.
 *
 * Never throws. Every step that can reject is captured into a `reason`.
 *
 * @returns `{ ok: true, data }` with the stored Flag, or `{ ok: false, reason }`
 */
export async function updatePracticeFlag({
  medplum,
  values,
  flagId,
  practiceId,
}: UpdatePracticeFlagProps): Promise<UpdatePracticeFlagResult> {
  const libraryGroup = await resolveLibraryGroupReference<UpdatePracticeFlagFailure>(
    medplum,
    practiceId,
    {
      lookupFailed: FlagFailure.LookupFailed,
      missingLibraryGroup: FlagFailure.MissingLibraryGroup,
    },
  );

  if (!libraryGroup.ok) {
    return { ok: false, reason: libraryGroup.reason };
  }

  const groupReference = libraryGroup.reference;

  const existing = await readLibraryFlag<UpdatePracticeFlagFailure>(
    medplum,
    flagId,
    groupReference,
    {
      lookupFailed: FlagFailure.LookupFailed,
      notFound: FlagFailure.NotFound,
    },
  );

  if (!existing.ok) {
    return { ok: false, reason: existing.reason };
  }

  const flagToWrite = buildUpdatedPracticeFlagResource(existing.flag, values, groupReference);
  const code = getLibraryFlagCode(flagToWrite);

  // Unreachable for parsed values, for the same reason as in `createPracticeFlag`.
  if (!code) {
    Sentry.captureException(new Error(MISSING_LIBRARY_FLAG_CODE_ERROR));
    return { ok: false, reason: FlagFailure.WriteFailed };
  }

  if (code !== getLibraryFlagCode(existing.flag)) {
    const codeCheck = await checkIfLibraryFlagCodeExists(medplum, groupReference, code);

    if (!codeCheck.ok) {
      return { ok: false, reason: codeCheck.reason };
    }

    if (codeCheck.exists) {
      return { ok: false, reason: FlagFailure.DuplicateName };
    }
  }

  let flag: Flag;
  try {
    flag = await medplum.updateResource(flagToWrite);
  } catch (error) {
    captureExceptionWithoutPhi("Failed to update a practice library flag", error, {
      flagId: flagToWrite.id,
    });
    return { ok: false, reason: FlagFailure.WriteFailed };
  }

  medplum.invalidateSearches("Flag");

  return { ok: true, data: flag };
}

/**
 * Whether an active entry other than the one being renamed already holds `code`.
 *
 * The entry itself matches its own code when nothing about the name changed, so it is excluded by
 * id — checking `length` alone would report every save as a duplicate.
 */
async function checkIfLibraryFlagCodeExists(
  medplum: MedplumClient,
  groupReference: string,
  code: string,
): Promise<{ ok: true; exists: boolean } | { ok: false; reason: UpdatePracticeFlagFailure }> {
  try {
    const matches = await medplum.searchResources(
      "Flag",
      buildLibraryFlagCodeQuery(groupReference, code),
      { cache: "no-cache" },
    );

    return { ok: true, exists: matches.length > 0 };
  } catch (error) {
    captureExceptionWithoutPhi("Failed to check a practice library flag code", error);
    return { ok: false, reason: FlagFailure.LookupFailed };
  }
}

/**
 * `LookupFailed` is the Group read or the entry read. `WriteFailed` leaves the entry in the library.
 * A delete cannot collide on a name, so there is no `DuplicateName`.
 */
export type DeletePracticeFlagFailure =
  | FlagFailure.LookupFailed
  | FlagFailure.MissingLibraryGroup
  | FlagFailure.NotFound
  | FlagFailure.WriteFailed;

export type DeletePracticeFlagResult =
  | { ok: true; data: Flag }
  | { ok: false; reason: DeletePracticeFlagFailure };

/**
 * Removes a library entry from a practice's library by deactivating it.
 *
 * The `Flag` is kept and its status set to `inactive`, rather than deleted. Two reasons: the entry
 * is what a chart flag reads its name and priority from, so erasing it would leave charts pointing
 * at nothing, and a library is small and hand-curated enough that an accidental delete should be
 * recoverable by an operator rather than gone.
 *
 * Deactivating is the whole operation. Charts carrying the flag are left alone here — showing it as
 * deprecated there is its own slice.
 *
 * Idempotent: deleting an entry that is already inactive writes nothing and reports success, so a
 * double-submit or a stale row cannot turn into an error the user has no way to act on.
 *
 * Never throws. Every step that can reject is captured into a `reason`.
 *
 * @returns `{ ok: true, data }` with the deactivated Flag, or `{ ok: false, reason }`
 */
export async function deletePracticeFlag({
  medplum,
  flagId,
  practiceId,
}: {
  medplum: MedplumClient;
  flagId: string;
  practiceId: string;
}): Promise<DeletePracticeFlagResult> {
  const libraryGroup = await resolveLibraryGroupReference<DeletePracticeFlagFailure>(
    medplum,
    practiceId,
    {
      lookupFailed: FlagFailure.LookupFailed,
      missingLibraryGroup: FlagFailure.MissingLibraryGroup,
    },
  );

  if (!libraryGroup.ok) {
    return { ok: false, reason: libraryGroup.reason };
  }

  const existing = await readLibraryFlag<DeletePracticeFlagFailure>(
    medplum,
    flagId,
    libraryGroup.reference,
    {
      lookupFailed: FlagFailure.LookupFailed,
      notFound: FlagFailure.NotFound,
    },
  );

  if (!existing.ok) {
    return { ok: false, reason: existing.reason };
  }

  if (existing.flag.status === "inactive") {
    return { ok: true, data: existing.flag };
  }

  let flag: Flag;
  try {
    flag = await medplum.updateResource({ ...existing.flag, status: "inactive" });
  } catch (error) {
    captureExceptionWithoutPhi("Failed to delete a practice library flag", error, {
      flagId: existing.flag.id,
    });
    return { ok: false, reason: FlagFailure.WriteFailed };
  }

  medplum.invalidateSearches("Flag");

  return { ok: true, data: flag };
}
